import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useResponsive } from "../../hooks/useResponsive";
import { spacing, colors, typography } from "../../utils/styling";
import CustomText from "../../components/atoms/CustomText";
import { View, Pressable } from "react-native-web";
import { CATEGORY_SECTIONS } from "../../utils/category_mappings";
import { resolveCoreSection } from "../../utils/quizinfo/personalityContent";
import PersonalitySidePanel from "../../components/personality/PersonalitySidePanel";
import {
  PERSONALITY_TYPES,
  PERSONALITY_COLORS,
  PERSONALITY_PHOTOS,
  toSlug,
  fromSlug,
} from "../../components/personality/personalityMeta";

function PersonalityOverview({ isMobile }: { isMobile: boolean }) {
  const navigate = useNavigate();

  return (
    <View
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <View
        style={{
          width: "100%",
          maxWidth: 900,
          backgroundColor: "rgba(255,255,255,0.72)",
          borderRadius: 20,
          padding: isMobile ? spacing.lg : spacing.xxl,
          marginBottom: spacing.xxl,
          alignItems: "center",
        }}
      >
        <CustomText
          style={{
            fontSize: isMobile ? 28 : 40,
            fontWeight: "700",
            color: colors.dark,
            fontFamily: typography.fontFamily.body,
            textAlign: "center",
            marginBottom: spacing.md,
          }}
        >
          Explore Our Personalities
        </CustomText>
        <CustomText
          style={{
            fontSize: isMobile ? 15 : 18,
            color: colors.gray[600],
            fontFamily: typography.fontFamily.body,
            textAlign: "center",
            maxWidth: 600,
            lineHeight: isMobile ? 24 : 28,
            marginBottom: spacing.xl,
          }}
        >
          There is no "right" way to think about money. The Money Personality
          framework identifies five distinct financial personalities, each shaped
          by different motivations, decision-making styles, and definitions of
          success.
        </CustomText>
      </View>

      <View
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: isMobile ? spacing.md : spacing.lg,
          maxWidth: 900,
          marginBottom: spacing.xxl,
        }}
      >
        {PERSONALITY_TYPES.map((type) => {
          const section = CATEGORY_SECTIONS[type];
          const color = PERSONALITY_COLORS[type];
          return (
            <Pressable
              key={type}
              style={({ hovered }: { hovered: boolean }) => ({
                width: isMobile ? "100%" : 260,
                backgroundColor: colors.white,
                borderRadius: 16,
                padding: spacing.lg,
                border: `2px solid ${hovered ? color : colors.gray[200]}`,
                boxShadow: hovered
                  ? `0 8px 24px ${color}25`
                  : "0 2px 8px rgba(0,0,0,0.06)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              })}
              onPress={() => navigate(`/personalities/${toSlug(type)}`)}
            >
              <View
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 32,
                  backgroundColor: color,
                  marginBottom: spacing.md,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CustomText
                  style={{
                    color: colors.white,
                    fontSize: 24,
                    fontWeight: "700",
                    fontFamily: typography.fontFamily.body,
                  }}
                >
                  {type.charAt(0)}
                </CustomText>
              </View>
              <CustomText
                style={{
                  fontSize: 18,
                  fontWeight: "700",
                  color: colors.dark,
                  fontFamily: typography.fontFamily.body,
                  textAlign: "center",
                  marginBottom: 6,
                }}
              >
                {type}
              </CustomText>
              <CustomText
                style={{
                  fontSize: 14,
                  fontWeight: "600",
                  color,
                  fontFamily: typography.fontFamily.body,
                  textAlign: "center",
                  marginBottom: spacing.sm,
                }}
              >
                {section.Subtext}
              </CustomText>
              <CustomText
                style={{
                  fontSize: 13,
                  color: colors.gray[600],
                  fontFamily: typography.fontFamily.body,
                  textAlign: "center",
                  lineHeight: 20,
                }}
                numberOfLines={3}
              >
                {section.Overview}
              </CustomText>
            </Pressable>
          );
        })}
      </View>

      <View
        style={{
          maxWidth: 600,
          alignItems: "center",
          marginBottom: spacing.xxl,
        }}
      >
        <CustomText
          style={{
            fontSize: isMobile ? 22 : 28,
            fontWeight: "700",
            color: colors.dark,
            fontFamily: typography.fontFamily.body,
            textAlign: "center",
            marginBottom: spacing.sm,
          }}
        >
          Which One Sounds Like You?
        </CustomText>
        <CustomText
          style={{
            fontSize: isMobile ? 15 : 16,
            color: colors.gray[600],
            fontFamily: typography.fontFamily.body,
            textAlign: "center",
            lineHeight: isMobile ? 24 : 26,
            marginBottom: spacing.lg,
          }}
        >
          Your assessment looks at your experiences, motivations, behaviors, and
          financial decision-making to uncover the patterns that make your
          relationship with money uniquely yours.
        </CustomText>
        <Pressable
          style={({ hovered }: { hovered: boolean }) => ({
            backgroundColor: hovered ? "#3a4c5b" : colors.primary,
            paddingVertical: spacing.md,
            paddingHorizontal: spacing.xl,
            borderRadius: 999,
            cursor: "pointer",
            transition: "all 0.2s ease",
          })}
          onPress={() => (window.location.href = "/quiz")}
        >
          <CustomText
            style={{
              color: colors.white,
              fontSize: 16,
              fontWeight: "700",
              fontFamily: typography.fontFamily.body,
            }}
          >
            Discover My Money Personality
          </CustomText>
        </Pressable>
      </View>
    </View>
  );
}

function PersonalityDetail({
  personalityName,
  isMobile,
}: {
  personalityName: string;
  isMobile: boolean;
}) {
  const navigate = useNavigate();
  const section = resolveCoreSection(personalityName, "PersonalityDetail");
  const color = PERSONALITY_COLORS[personalityName] || colors.primary;

  if (!section.Subtext) {
    return (
      <View style={{ padding: spacing.xl, alignItems: "center" }}>
        <CustomText
          style={{
            fontSize: 24,
            fontWeight: "700",
            color: colors.dark,
            fontFamily: typography.fontFamily.body,
          }}
        >
          Personality not found
        </CustomText>
        <Pressable
          onPress={() => navigate("/personalities")}
          style={{ marginTop: spacing.md, cursor: "pointer" }}
        >
          <CustomText
            style={{ color: colors.primary, fontFamily: typography.fontFamily.body }}
          >
            View all personalities
          </CustomText>
        </Pressable>
      </View>
    );
  }

  const attributeCards = [
    { label: "Driven By", value: section.DrivenBy },
    { label: "Measures Success By", value: section.MeasuresSuccessBy },
    { label: "Natural Strength", value: section.NaturalStrength },
  ].filter((a) => a.value);

  const photos = PERSONALITY_PHOTOS[personalityName] || [];
  const sidePanel = (
    <View style={{ width: "100%" }}>
      <CustomText
        style={{
          fontSize: 13,
          fontWeight: "700",
          color: colors.gray[600],
          fontFamily: typography.fontFamily.body,
          textTransform: "uppercase",
          letterSpacing: 1.2,
          marginBottom: spacing.sm,
        }}
      >
        Put it into action
      </CustomText>
      <PersonalitySidePanel personality={personalityName} layout="column" />
    </View>
  );

  return (
    <View
      style={{
        width: "100%",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "flex-start",
        gap: spacing.xl,
      }}
    >
    <View style={{ width: "100%", flex: isMobile ? undefined : 1, minWidth: 0 }}>
      <Pressable
        onPress={() => navigate("/personalities")}
        style={{
          marginBottom: spacing.lg,
          cursor: "pointer",
          flexDirection: "row",
          alignItems: "center",
          gap: spacing.xs,
        }}
      >
        <CustomText
          style={{
            color: colors.gray[500],
            fontSize: 14,
            fontFamily: typography.fontFamily.body,
          }}
        >
          ← All Personalities
        </CustomText>
      </Pressable>

      {/* Hero */}
      <View
        style={{
          backgroundColor: colors.white,
          borderRadius: 20,
          padding: isMobile ? spacing.lg : spacing.xxl,
          marginBottom: spacing.xl,
          borderTop: `4px solid ${color}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <CustomText
          style={{
            fontSize: 14,
            color: colors.gray[500],
            fontFamily: typography.fontFamily.body,
            marginBottom: 4,
          }}
        >
          About the
        </CustomText>
        <CustomText
          style={{
            fontSize: isMobile ? 36 : 48,
            fontWeight: "700",
            color: colors.dark,
            fontFamily: typography.fontFamily.body,
            marginBottom: spacing.xs,
          }}
        >
          {personalityName}
        </CustomText>
        <CustomText
          style={{
            fontSize: isMobile ? 18 : 22,
            fontWeight: "600",
            color,
            fontFamily: typography.fontFamily.body,
            marginBottom: spacing.lg,
          }}
        >
          {section.Subtext}
        </CustomText>
        <CustomText
          style={{
            fontSize: isMobile ? 15 : 17,
            color: colors.gray[700],
            fontFamily: typography.fontFamily.body,
            lineHeight: isMobile ? 24 : 28,
          }}
        >
          {section.Overview}
        </CustomText>
        {photos.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 10,
              overflowX: "auto",
              marginTop: spacing.lg,
              paddingBottom: 6,
              scrollSnapType: "x mandatory",
            }}
          >
            {photos.map((src) => (
              <img
                key={src}
                src={src}
                alt=""
                loading="lazy"
                style={{
                  height: isMobile ? 180 : 220,
                  width: "auto",
                  borderRadius: 12,
                  flexShrink: 0,
                  objectFit: "cover",
                  scrollSnapAlign: "start",
                }}
              />
            ))}
          </div>
        )}
      </View>

      {isMobile && <View style={{ marginBottom: spacing.xl }}>{sidePanel}</View>}

      {/* Attribute cards */}
      {attributeCards.length > 0 && (
        <View
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: spacing.md,
            marginBottom: spacing.xl,
          }}
        >
          {attributeCards.map(({ label, value }) => (
            <View
              key={label}
              style={{
                flex: 1,
                backgroundColor: colors.white,
                borderRadius: 16,
                padding: spacing.lg,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                alignItems: "center",
              }}
            >
              <CustomText
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: colors.gray[500],
                  fontFamily: typography.fontFamily.body,
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  marginBottom: 6,
                }}
              >
                {label}
              </CustomText>
              <CustomText
                style={{
                  fontSize: isMobile ? 18 : 20,
                  fontWeight: "700",
                  color: colors.dark,
                  fontFamily: typography.fontFamily.body,
                  textAlign: "center",
                }}
              >
                {value}
              </CustomText>
            </View>
          ))}
        </View>
      )}

      {/* Watch-Out */}
      {section.WatchOut && (
        <View
          style={{
            backgroundColor: colors.warning + "12",
            border: `1px solid ${colors.warning}35`,
            borderRadius: 16,
            padding: spacing.lg,
            marginBottom: spacing.xl,
          }}
        >
          <CustomText
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: colors.warning,
              fontFamily: typography.fontFamily.body,
              textTransform: "uppercase",
              letterSpacing: 1.2,
              marginBottom: 6,
            }}
          >
            Watch-Out
          </CustomText>
          <CustomText
            style={{
              fontSize: isMobile ? 15 : 16,
              color: colors.gray[700],
              fontFamily: typography.fontFamily.body,
              lineHeight: isMobile ? 24 : 26,
            }}
          >
            {section.WatchOut}
          </CustomText>
        </View>
      )}

      {/* Strengths + Weaknesses side by side */}
      <View
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: spacing.md,
          marginBottom: spacing.xl,
        }}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 16,
            padding: spacing.lg,
          }}
        >
          <CustomText
            style={{
              fontSize: isMobile ? 18 : 20,
              fontWeight: "700",
              color: "#166534",
              fontFamily: typography.fontFamily.body,
              marginBottom: spacing.md,
            }}
          >
            ✓ Strengths
          </CustomText>
          {section.Strengths.map((s, i) => (
            <CustomText
              key={i}
              style={{
                fontSize: isMobile ? 14 : 15,
                color: "#15803d",
                fontFamily: typography.fontFamily.body,
                lineHeight: 22,
                marginBottom: spacing.sm,
              }}
            >
              {s}
            </CustomText>
          ))}
        </View>

        <View
          style={{
            flex: 1,
            backgroundColor: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: 16,
            padding: spacing.lg,
          }}
        >
          <CustomText
            style={{
              fontSize: isMobile ? 18 : 20,
              fontWeight: "700",
              color: "#92400e",
              fontFamily: typography.fontFamily.body,
              marginBottom: spacing.md,
            }}
          >
            ⚠ Watch-Outs
          </CustomText>
          {section.Weaknesses.map((w, i) => (
            <CustomText
              key={i}
              style={{
                fontSize: isMobile ? 14 : 15,
                color: "#a16207",
                fontFamily: typography.fontFamily.body,
                lineHeight: 22,
                marginBottom: spacing.sm,
              }}
            >
              {w}
            </CustomText>
          ))}
        </View>
      </View>

      {/* Growth Opportunities */}
      <View
        style={{
          backgroundColor: colors.white,
          borderRadius: 16,
          padding: spacing.lg,
          marginBottom: spacing.xl,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <CustomText
          style={{
            fontSize: isMobile ? 18 : 20,
            fontWeight: "700",
            color: colors.dark,
            fontFamily: typography.fontFamily.body,
            marginBottom: spacing.md,
          }}
        >
          Growth Opportunities
        </CustomText>
        {section.GrowthOpportunities.map((g, i) => (
          <View
            key={i}
            style={{
              backgroundColor: "#f0fdf4",
              borderRadius: 8,
              padding: spacing.md,
              marginBottom: spacing.sm,
            }}
          >
            <CustomText
              style={{
                fontSize: isMobile ? 14 : 15,
                color: colors.gray[700],
                fontFamily: typography.fontFamily.body,
                lineHeight: 22,
              }}
            >
              {g}
            </CustomText>
          </View>
        ))}
      </View>

      {/* Behavioral Concepts */}
      <View
        style={{
          backgroundColor: colors.white,
          borderRadius: 16,
          padding: spacing.lg,
          marginBottom: spacing.xl,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <CustomText
          style={{
            fontSize: isMobile ? 18 : 20,
            fontWeight: "700",
            color: colors.dark,
            fontFamily: typography.fontFamily.body,
            marginBottom: spacing.md,
          }}
        >
          Behavioral Concepts
        </CustomText>
        {section.BehavioralConcepts.map((b, i) => (
          <View
            key={i}
            style={{
              backgroundColor: "#fdf2f8",
              borderRadius: 8,
              padding: spacing.md,
              marginBottom: spacing.sm,
            }}
          >
            <CustomText
              style={{
                fontSize: isMobile ? 14 : 15,
                color: colors.gray[700],
                fontFamily: typography.fontFamily.body,
                lineHeight: 22,
              }}
            >
              {b}
            </CustomText>
          </View>
        ))}
      </View>

      {/* CTA */}
      <View style={{ alignItems: "center", marginBottom: spacing.xxl }}>
        <Pressable
          style={({ hovered }: { hovered: boolean }) => ({
            backgroundColor: hovered ? "#3a4c5b" : colors.primary,
            paddingVertical: spacing.md,
            paddingHorizontal: spacing.xl,
            borderRadius: 999,
            cursor: "pointer",
            transition: "all 0.2s ease",
          })}
          onPress={() => (window.location.href = "/quiz")}
        >
          <CustomText
            style={{
              color: colors.white,
              fontSize: 16,
              fontWeight: "700",
              fontFamily: typography.fontFamily.body,
            }}
          >
            Discover My Money Personality
          </CustomText>
        </Pressable>
      </View>
    </View>
    {!isMobile && (
      <View style={{ width: 320, flexShrink: 0, position: "sticky" as any, top: 120 }}>
        {sidePanel}
      </View>
    )}
    </View>
  );
}

export default function GeneralPersonalityPage() {
  const { personality } = useParams();
  const { isMobile } = useResponsive();

  const resolvedPersonality = personality ? fromSlug(personality) : null;

  // mp-fade-bg: the homepage's colour fade (10/7 call). caretColor: the page
  // has no inputs, so hide the text caret that showed when a paragraph was
  // clicked and made it look editable (Nina, 10/7 call).
  return (
    <div className="mp-fade-bg" style={{ width: "100%", caretColor: "transparent", minHeight: "100vh" }}>
    <View
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: isMobile ? spacing.md : spacing.xxl,
        paddingLeft: isMobile ? spacing.lg : spacing.xxl,
        paddingRight: isMobile ? spacing.lg : spacing.xxl,
        // Clears the fixed header so the back link is not hidden under it.
        marginTop: isMobile ? 80 : 110,
        marginBottom: spacing.xxl,
      }}
    >
      <View
        style={{
          width: "100%",
          maxWidth: 1100,
          paddingBottom: isMobile ? spacing.xxl : 0,
        }}
      >
        {resolvedPersonality ? (
          <PersonalityDetail
            personalityName={resolvedPersonality}
            isMobile={isMobile}
          />
        ) : (
          <PersonalityOverview isMobile={isMobile} />
        )}
      </View>
    </View>
    </div>
  );
}
